import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AlertCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCompleteGoogleCallback } from "@/lib/google";

/**
 * Google redirects here after OAuth consent.
 * URL shape: /auth/callback/google_calendar?code=...&state=...
 *
 * The page exchanges the code for tokens via relay-google-auth-callback,
 * then bounces to /settings?tab=integrations on success. Errors stay here
 * so the user sees what went wrong.
 */
export default function AuthCallbackGoogle() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const complete = useCompleteGoogleCallback();
  const [error, setError] = useState<string | null>(null);
  // The OAuth code is single-use. Guard against React StrictMode's double
  // effect-invocation (and any remount) so we never exchange the code twice —
  // a second exchange fails with invalid_grant and would overwrite a
  // successful connect's UI with a spurious error.
  const exchangeStarted = useRef(false);

  useEffect(() => {
    if (exchangeStarted.current) return;
    exchangeStarted.current = true;

    const code = searchParams.get("code");
    const state = searchParams.get("state");
    const oauthError = searchParams.get("error");
    const oauthErrorDescription = searchParams.get("error_description");

    // Google can redirect with ?error= when the user cancels or denies consent.
    if (oauthError) {
      const description = oauthErrorDescription ?? oauthError;
      setError(description);
      return;
    }

    if (!code || !state) {
      setError("Missing OAuth code or state in the callback URL.");
      return;
    }

    complete.mutate(
      { code, state },
      {
        onSuccess: () => {
          navigate("/settings?tab=integrations", { replace: true });
        },
        onError: (e) => {
          const message = e instanceof Error ? e.message : "Connection failed";
          setError(message);
        },
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex min-h-screen items-center justify-center px-6 py-10">
      {error ? (
        <div className="max-w-md space-y-4">
          <div className="rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
            <AlertCircle className="mr-2 inline h-4 w-4" />
            {error}
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => navigate("/settings?tab=integrations", { replace: true })}
          >
            Back to Google settings
          </Button>
        </div>
      ) : (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Completing Google connection…
        </div>
      )}
    </div>
  );
}
